<template>
	<template v-if="['create-chat', 'edit-chat'].includes(mode)">
		<chat-create-dialog-panel />
	</template>
	<template v-else>
		<div class="vertical-container" ref="containerRef">
			<div
				class="vertical-block top-block"
				:class="{ 'collapsed': collapsedTop }"
				:style="{
					height: (collapsedTop ? collapsedTopHeight : topBlockHeight) + 'px',
					minHeight: collapsedTop ? collapsedTopHeight + 'px' : '32px'
				}"
			>
				<app-tooltip
					v-if="collapsedTop && isCompactMode"
					class="collapsed-block-tooltip"
					:text="$t('ui_expand')"
				>
					<template #activator>
						<div
							class="collapsed-block"
							@click="toggleBlock('top')"
						>
							{{ $t('chat_count_label') }}:&nbsp;
							<span class="collapsed-block__count">{{ dialogs.length }}</span>
							<app-icon class="collapsed-block__icon" name="full-screen" width="14px" color="#979797" />
						</div>
					</template>
				</app-tooltip>
				<div v-else class="block-content" :style="{ height: '100%' }">
					<div
						v-if="isChatsDenied"
						class="chat-panel__no-access"
					>
						<app-icon
							class="chat-panel__no-access-icon"
							name="invalid"
							width="24px"
							color="#979797"
						/>
						<div class="chat-panel__no-access-label">
							{{ $t('chat_no_access') }}
						</div>
					</div>
					<template v-else>
						<chat-search-panel
							v-model="searchStr"
							@request-focus="focusMessageInput"
						/>
						<chat-search-list
							v-if="isGlobalSearchStarted"
							:search="searchStr"
							@on-open-dialog="onOpenDialog"
							@on-open-message="onOpenDialog"
						/>
						<chat-messages-panel
							v-else-if="isMessagesSearchStarted"
							ref="messagesPanelRef"
							:mode="mode"
							@on-open-message="onOpenSearchedMessage"
						/>
						<component
							v-else
							:is="modeComponent[mode]"
							ref="messagesPanelRef"
							:loading="loading"
							:dialogs="dialogs"
							:mode="mode"
							:key="mode"
							@on-open="onOpenDialog"
							@on-delete="onDeleteDialog"
							@on-open-message="onOpenSearchedMessage"
						/>
					</template>
				</div>
			</div>

			<!-- Вертикальный ресайзер -->
			<div
				class="vertical-resizer"
				:class="{ 'vertical-resizer--disabled': isCompactMode }"
				@mousedown="startVerticalResize($event)"
			/>

			<div
				class="vertical-block bottom-block"
				:class="{ 'collapsed': collapsedBottom }"
				:style="{
					height: (collapsedBottom ? collapsedBottomHeight : bottomBlockHeight) + 'px',
					minHeight: collapsedBottom ? collapsedBottomHeight + 'px' : effectiveMinHeight + 'px',
					maxHeight: (collapsedBottom ? collapsedBottomHeight : (isCompactMode ? bottomBlockHeight : maxBottomBlockHeight)) + 'px'
				}"
			>
				<app-tooltip
					v-if="collapsedBottom && isCompactMode"
					class="collapsed-block-tooltip"
					:text="$t('ui_expand')"
				>
					<template #activator>
						<div
							class="collapsed-block"
							@click="toggleBlock('bottom')"
						>
							{{ $t('chat_favourite_count_label') }}:&nbsp;
							<span class="collapsed-block__count">{{ favouritesData?.length || 0 }}</span>
							<app-icon class="collapsed-block__icon" name="full-screen" width="14px" color="#979797" />
						</div>
					</template>
				</app-tooltip>
				<div v-else class="block-content" :style="{ height: '100%' }">
					<chat-favourite-panel />
				</div>
			</div>

			<!-- Визуальный вертикальный ресайзер -->
			<div
				v-if="isVerticalResizing"
				class="visual-resizer vertical"
				:style="{
					top: visualVerticalResizerTop + 'px',
					left: visualVerticalResizerLeft + 'px',
					width: visualVerticalResizerWidth + 'px'
				}"
			/>
		</div>
	</template>
</template>

<script setup lang="ts">
import {
	computed,
	watch,
	onMounted,
	ref,
	onUnmounted,
	nextTick
} from "vue";
import {
	useRoute,
	useRouter
} from "vue-router";

import ChatFavouritePanel from "@/components/main/chat-panel/ChatFavouritePanel.vue";
import ChatSearchPanel from "@/components/main/chat-panel/ChatSearchPanel.vue";
import ChatDialogsPanel from "@/components/main/chat-panel/ChatDialogsPanel.vue";
import ChatMessagesPanel from "@/components/main/chat-panel/ChatMessagesPanel.vue";
import ChatCreateDialogPanel from "@/components/main/chat-panel/ChatCreateDialogPanel.vue";
import {
	ChatMessage,
	chatsStore,
	Dialog
} from "@/store/chats";
import {ChatsApi, SearchDialog, SearchMessage} from "@/ api/ChatsApi";
import { debounce } from "lodash";
import ChatSearchList from "@/components/main/chat-panel/ChatSearchList.vue";
import { favouritesStore, Favourite } from "@/store/favourites";
import { getCurrentLocalDateTime } from "@/utils/auxfunc";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { useBreakpoints } from "@/composables/useBreakpoints";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

const route = useRoute()
const router = useRouter()
const store = chatsStore()
const favouriteStore = favouritesStore()

const searchStr = ref('')
const isGlobalSearchStarted = ref(false);
const isMessagesSearchStarted = ref(false);
const dialogs = computed(() => store.getDialogs)
const openedChatId = computed(() => store.getOpenedChatId)
const mode = computed(() => store.getChatMode)
const isChatsDenied = computed(() => store.isChatsAccessDenied)

const initialBottomHeight = 32
const minBlockHeight = 32
const collapsedTopHeight = 32
const collapsedBottomHeight = 32
const persist = true
const storageKey = 'chat-block-positions_v1.1'

const containerRef = ref<HTMLElement | null>(null)
const containerHeight = ref(0)

const isInitialDataLoaded = ref(false)
const isAdjustingHeight = ref(false)
let adjustHeightTimer: ReturnType<typeof setTimeout> | null = null

// Высоты блоков
const topBlockHeight = ref(0)
const bottomBlockHeight = ref(initialBottomHeight)

// Переменные для вертикального ресайза
const isVerticalResizing = ref(false)
const startY = ref(0)
const startTopHeight = ref(0)
const startBottomHeight = ref(0)

// Визуальный ресайзер
const visualVerticalResizerTop = ref(0)
const visualVerticalResizerLeft = ref(0)
const visualVerticalResizerWidth = ref(0)

// Collapse/expand состояние (для compact mode)
const collapsedTop = ref(false)
const collapsedBottom = ref(false)

const { isMobile, isTablet } = useBreakpoints()
const isCompactMode = computed(() => isMobile.value || isTablet.value)

const favouritesData = computed(() => favouriteStore.favouritesList)
const isFavouritesLoading = computed(() => favouriteStore.isFavouritesLoading)

const hasFavourites = computed(() => {
	return favouritesData.value !== null && favouritesData.value.length > 0
})

const DEFAULT_MIN_HEIGHT = 32
const EXPANDED_MIN_HEIGHT = 100

const effectiveMinHeight = computed(() => {
	return hasFavourites.value ? EXPANDED_MIN_HEIGHT : DEFAULT_MIN_HEIGHT
})

const messagesPanelRef = ref<any>(null)

const focusMessageInput = () => {
	messagesPanelRef.value?.focusSync?.()
}

const modeComponent = computed(() => {
	return {
		list: ChatDialogsPanel,
		chat: ChatMessagesPanel,
		'search-messages': ChatMessagesPanel,
		'create-chat': ChatCreateDialogPanel,
		'edit-chat': ChatCreateDialogPanel,
		search: ChatDialogsPanel,
	} as Record<string, any>
})

const onOpenDialog = async (dialog: Dialog | SearchDialog | SearchMessage, isCreated = false) => {
	focusMessageInput()
	searchStr.value = '';
	// Если перешли из поиска
	if ('ChatId' in dialog) {
		await router.push({
			path: '/',
			query: {
				...route.query,
				mode: 'chat',
				id: dialog.ChatId,
				message: dialog.Id
			}
		});
		store.addOpenedChatsHistory(dialogs.value.find(d => d.Id === dialog.ChatId) as Dialog)
		store.setMode('chat')
		store.setOpenedChat(dialog.ChatId, dialog.Id)
	} else {
		await router.push({
			path: '/',
			query: {
				...route.query,
				mode: 'chat',
				id: dialog.Id
			}
		});
		store.addOpenedChatsHistory({
			Id: dialog.Id,
			Name: dialog.Name,
			ScrollTop: null
		})
		store.setMode('chat')
		store.setOpenedChat(dialog.Id)
		if (isCreated) {
			try {
				await store.updateDialogInList(getCurrentLocalDateTime())
			} catch (e) {
				console.error(e)
			}
		}
	}
}

const onOpenSearchedMessage = async (message: ChatMessage) => {
	isMessagesSearchStarted.value = false
	store.setMode('chat')
	await store.setOpenedChat(openedChatId.value, message.Id)
}

const onDeleteDialog = async (dialogId: number) => {
	try {
		await ChatsApi.deleteDialog(dialogId)
		await store.fetchDialogs()
	} catch (e) {
		console.error(e)
	}
}

const onOpenGlobalSearch = async () => {
	if (!isGlobalSearchStarted.value && searchStr.value) {
		isGlobalSearchStarted.value = true
	}
}

const onCloseGlobalSearch = () => {
	isGlobalSearchStarted.value = false
}

const onSearchMessagesInChat = async () => {
	if (searchStr.value) {
		if (mode.value !== 'search-messages') {
			store.setMode('search-messages')
		}

		if (!isMessagesSearchStarted.value) {
			isMessagesSearchStarted.value = true
		}

		if (openedChatId.value) {
			await store.fetchSearchedMessages(openedChatId.value, searchStr.value)
		}
	}
}

const onCloseMessagesSearch = () => {
	isMessagesSearchStarted.value = false
	store.setMode('chat')
	store.setOpenedChat(openedChatId.value, null, true)
}

const debouncedGlobalSearch = debounce(onOpenGlobalSearch, 500)
const debouncedMessagesSearch = debounce(onSearchMessagesInChat, 500)

watch(
	searchStr,
	(newVal) => {
		switch (route.query.mode) {
			case 'list':
				if (newVal) {
					debouncedGlobalSearch()
				} else {
					onCloseGlobalSearch()
				}
				return
			case 'chat':
			case 'search-messages': {
				if (newVal) {
					debouncedMessagesSearch()
				} else {
					onCloseMessagesSearch()
				}
				return
			}
		}
	}
);

// Максимальная высота нижнего блока (половина контейнера)
const maxBottomBlockHeight = computed(() => {
	return Math.floor(containerHeight.value / 2)
})

// Функция для корректировки высоты под минимальную
const adjustHeightToMin = (newMinHeight: number) => {
	if (!containerRef.value || isVerticalResizing.value || isAdjustingHeight.value) return
	if (isMobile.value && (collapsedTop.value || collapsedBottom.value)) return

	isAdjustingHeight.value = true

	try {
		const currentMaxHeight = maxBottomBlockHeight.value
		const targetMinHeight = Math.min(newMinHeight, currentMaxHeight)

		if (bottomBlockHeight.value < targetMinHeight) {
			// Случай 1: Появились данные, нужно увеличить блок
			const newBottomHeight = targetMinHeight
			const newTopHeight = containerHeight.value - newBottomHeight

			if (newTopHeight >= minBlockHeight) {
				bottomBlockHeight.value = newBottomHeight
				topBlockHeight.value = newTopHeight
			} else {
				topBlockHeight.value = minBlockHeight
				bottomBlockHeight.value = containerHeight.value - minBlockHeight
			}
		} else if (!hasFavourites.value && bottomBlockHeight.value > DEFAULT_MIN_HEIGHT) {
			// Случай 2: Данных нет, нужно уменьшить блок до 32px
			const newBottomHeight = DEFAULT_MIN_HEIGHT
			const newTopHeight = containerHeight.value - newBottomHeight

			if (newTopHeight >= minBlockHeight) {
				bottomBlockHeight.value = newBottomHeight
				topBlockHeight.value = newTopHeight
			}
		}
	} finally {
		if (adjustHeightTimer) {
			clearTimeout(adjustHeightTimer)
		}

		adjustHeightTimer = setTimeout(() => {
			isAdjustingHeight.value = false
			adjustHeightTimer = null
		}, 100)
	}
}

const loadHeightsFromStorage = (): { top: number; bottom: number; collapsedTop?: boolean; collapsedBottom?: boolean } | null => {
	if (!persist) return null

	try {
		const saved = localStorage.getItem(storageKey)
		if (saved) {
			const parsed = JSON.parse(saved)
			const { top, bottom } = parsed
			if (typeof top === 'number' && typeof bottom === 'number' &&
				!isNaN(top) && !isNaN(bottom)) {
				return {
					top,
					bottom,
					collapsedTop: parsed.collapsedTop || false,
					collapsedBottom: parsed.collapsedBottom || false
				}
			}
		}
	} catch (error) {
		console.warn('Не удалось загрузить высоты из localStorage:', error)
	}
	return null
}

const saveHeightsToStorage = () => {
	if (!persist || isAdjustingHeight.value) return

	try {
		if (typeof topBlockHeight.value !== 'number' || typeof bottomBlockHeight.value !== 'number' ||
			isNaN(topBlockHeight.value) || isNaN(bottomBlockHeight.value)) {
			return
		}

		const heightsToSave = {
			top: Math.round(topBlockHeight.value),
			bottom: Math.round(bottomBlockHeight.value),
			collapsedTop: collapsedTop.value,
			collapsedBottom: collapsedBottom.value
		}
		localStorage.setItem(storageKey, JSON.stringify(heightsToSave))
	} catch (error) {
		console.warn('Не удалось сохранить высоты в localStorage:', error)
	}
}

// Инициализация высот
const initializeHeights = () => {
	if (!containerRef.value) return

	containerHeight.value = containerRef.value.clientHeight

	if (containerHeight.value <= 0) return

	// Пытаемся загрузить сохраненные высоты
	const savedHeights = loadHeightsFromStorage()

	if (savedHeights) {
		// Восстанавливаем collapsed состояние (сохраняем даже при невалидных высотах)
		const savedCollapsedTop = savedHeights.collapsedTop || false
		const savedCollapsedBottom = savedHeights.collapsedBottom || false
		collapsedTop.value = savedCollapsedTop
		collapsedBottom.value = savedCollapsedBottom

		// В compact mode с сохранённым collapsed — пересчитываем высоты по collapsed state
		if (isCompactMode.value && (savedCollapsedTop || savedCollapsedBottom)) {
			if (savedCollapsedTop) {
				topBlockHeight.value = collapsedTopHeight
				bottomBlockHeight.value = containerHeight.value - collapsedTopHeight
			} else {
				bottomBlockHeight.value = collapsedBottomHeight
				topBlockHeight.value = containerHeight.value - collapsedBottomHeight
			}
			return
		}

		const totalHeight = savedHeights.top + savedHeights.bottom
		const isValid = Math.abs(totalHeight - containerHeight.value) < 1 &&
			savedHeights.top >= minBlockHeight &&
			savedHeights.bottom <= maxBottomBlockHeight.value

		if (isValid) {
			topBlockHeight.value = savedHeights.top
			bottomBlockHeight.value = savedHeights.bottom

			// После загрузки сохраненных высот проверяем, нужна ли корректировка
			// но только если данные уже загружены
			if (isInitialDataLoaded.value) {
				nextTick(() => {
					adjustHeightToMin(effectiveMinHeight.value)
				})
			}
		} else {
			// Невалидные высоты — сбрасываем collapsed и используем дефолт
			collapsedTop.value = false
			collapsedBottom.value = false
			const initialMinHeight = effectiveMinHeight.value
			bottomBlockHeight.value = Math.min(initialMinHeight, maxBottomBlockHeight.value)
			topBlockHeight.value = containerHeight.value - bottomBlockHeight.value
		}
	} else {
		// Если нет сохраненных - используем минимальную высоту
		const initialMinHeight = effectiveMinHeight.value
		bottomBlockHeight.value = Math.min(initialMinHeight, maxBottomBlockHeight.value)
		topBlockHeight.value = containerHeight.value - bottomBlockHeight.value
	}

	// В compact mode всегда один свёрнут, другой развёрнут
	if (isCompactMode.value && !collapsedTop.value && !collapsedBottom.value) {
		collapsedBottom.value = true
		collapsedTop.value = false
		topBlockHeight.value = containerHeight.value - collapsedBottomHeight
		bottomBlockHeight.value = collapsedBottomHeight
	}
}

const startVerticalResize = (event: MouseEvent) => {
	if (isCompactMode.value) return
	event.preventDefault()

	if (!containerRef.value) return

	const containerRect = containerRef.value.getBoundingClientRect()
	visualVerticalResizerLeft.value = containerRect.left
	visualVerticalResizerWidth.value = containerRect.width

	isVerticalResizing.value = true
	startY.value = event.clientY
	startTopHeight.value = topBlockHeight.value
	startBottomHeight.value = bottomBlockHeight.value

	visualVerticalResizerTop.value = containerRect.top + topBlockHeight.value

	document.addEventListener('mousemove', handleVerticalResizeMove)
	document.addEventListener('mouseup', stopVerticalResize)
	document.body.style.userSelect = 'none'
	document.body.style.cursor = 'row-resize'
}

let resizeRafId: number | null = null
let lastMoveEvent: MouseEvent | null = null

const cancelResizeRaf = () => {
	if (resizeRafId !== null) {
		cancelAnimationFrame(resizeRafId)
		resizeRafId = null
	}
}

const applyVerticalResize = (event: MouseEvent) => {
	if (!isVerticalResizing.value || !containerRef.value) {
		return
	}

	const deltaY = event.clientY - startY.value

	let newTopHeight = startTopHeight.value + deltaY
	let newBottomHeight = startBottomHeight.value - deltaY

	const currentMinHeight = effectiveMinHeight.value
	const currentMaxHeight = maxBottomBlockHeight.value

	// Проверяем минимальные высоты
	if (newTopHeight < minBlockHeight) {
		newTopHeight = minBlockHeight
		newBottomHeight = containerHeight.value - minBlockHeight
	}

	if (newBottomHeight < currentMinHeight) {
		newBottomHeight = currentMinHeight
		newTopHeight = containerHeight.value - currentMinHeight
	}

	// Проверяем максимальную высоту
	if (newBottomHeight > currentMaxHeight) {
		newBottomHeight = currentMaxHeight
		newTopHeight = containerHeight.value - currentMaxHeight
	}

	// Проверяем верхний блок
	if (newTopHeight < minBlockHeight) {
		newTopHeight = minBlockHeight
		newBottomHeight = containerHeight.value - minBlockHeight
	}

	// Обновляем позицию визуального ресайзера
	const containerRect = containerRef.value.getBoundingClientRect()
	visualVerticalResizerTop.value = containerRect.top + newTopHeight

	// Обновляем высоты
	topBlockHeight.value = Math.round(newTopHeight)
	bottomBlockHeight.value = Math.round(newBottomHeight)
}

const handleVerticalResizeMove = (event: MouseEvent) => {
	lastMoveEvent = event
	if (resizeRafId !== null) {
		return
	}
	resizeRafId = requestAnimationFrame(() => {
		resizeRafId = null
		if (lastMoveEvent) {
			applyVerticalResize(lastMoveEvent)
		}
	})
}

const stopVerticalResize = () => {
	if (!isVerticalResizing.value) {
		return
	}

	// Флашим последний отложенный кадр, чтобы итоговая позиция была точной
	if (resizeRafId !== null) {
		cancelResizeRaf()
		if (lastMoveEvent) {
			applyVerticalResize(lastMoveEvent)
		}
	}

	isVerticalResizing.value = false

	// Сохраняем после ресайза
	saveHeightsToStorage()

	document.removeEventListener('mousemove', handleVerticalResizeMove)
	document.removeEventListener('mouseup', stopVerticalResize)
	document.body.style.userSelect = ''
	document.body.style.cursor = ''
}

const toggleBlock = (block: 'top' | 'bottom') => {
	if (!containerRef.value) return

	if (block === 'top') {
		if (!collapsedTop.value) return
		collapsedTop.value = false
		collapsedBottom.value = true
		topBlockHeight.value = containerHeight.value - collapsedBottomHeight
		bottomBlockHeight.value = collapsedBottomHeight
	} else {
		if (!collapsedBottom.value) return
		collapsedBottom.value = false
		collapsedTop.value = true
		bottomBlockHeight.value = containerHeight.value - collapsedTopHeight
		topBlockHeight.value = collapsedTopHeight
	}

	saveHeightsToStorage()
}

// Наблюдаем за изменениями высот и сохраняем (с debounce)
const debouncedSaveHeights = debounce(() => {
	if (!isVerticalResizing.value && !isAdjustingHeight.value) {
		saveHeightsToStorage()
	}
}, 300)

watch([topBlockHeight, bottomBlockHeight], () => {
	debouncedSaveHeights()
})

watch(favouritesData, async (newVal) => {
	if (!isInitialDataLoaded.value) {
		if (newVal !== null) {
			isInitialDataLoaded.value = true

			if (containerRef.value && !isVerticalResizing.value) {
				await nextTick()
				adjustHeightToMin(effectiveMinHeight.value)
			}
		}
		return
	}

	if (!containerRef.value || isVerticalResizing.value) return

	await nextTick()
	adjustHeightToMin(effectiveMinHeight.value)
}, { deep: true })

watch(isFavouritesLoading, (loading) => {
	if (!loading && !isInitialDataLoaded.value && favouritesData.value !== null) {
		isInitialDataLoaded.value = true

		if (containerRef.value && !isVerticalResizing.value) {
			nextTick(() => {
				adjustHeightToMin(effectiveMinHeight.value)
			})
		}
	}
})

// При переходе в compact mode — принудительно один свёрнут
watch(isCompactMode, (compact) => {
	if (compact) {
		collapsedBottom.value = true
		collapsedTop.value = false
		topBlockHeight.value = containerHeight.value - collapsedBottomHeight
		bottomBlockHeight.value = collapsedBottomHeight
		saveHeightsToStorage()
	} else {
		collapsedTop.value = false
		collapsedBottom.value = false
		initializeHeights()
	}
})

const handleResizeWindow = debounce(() => {
	if (!containerRef.value) return

	// В compact mode с collapsed блоками — просто пересчитываем высоты
	// без вызова initializeHeights, чтобы не сбросить collapsed состояние
	if (isCompactMode.value && (collapsedTop.value || collapsedBottom.value)) {
		containerHeight.value = containerRef.value.clientHeight
		if (collapsedTop.value) {
			topBlockHeight.value = collapsedTopHeight
			bottomBlockHeight.value = containerHeight.value - collapsedTopHeight
		} else {
			bottomBlockHeight.value = collapsedBottomHeight
			topBlockHeight.value = containerHeight.value - collapsedBottomHeight
		}
		saveHeightsToStorage()
		return
	}

	initializeHeights()
}, 150)

onMounted(() => {
	store.fetchDialogs()

	nextTick(() => {
		initializeHeights()
	})

	window.addEventListener('resize', handleResizeWindow)
})

onUnmounted(() => {
	cancelResizeRaf()
	document.removeEventListener('mousemove', handleVerticalResizeMove)
	document.removeEventListener('mouseup', stopVerticalResize)
	window.removeEventListener('resize', handleResizeWindow)
	debouncedSaveHeights.cancel()
	handleResizeWindow.cancel()
	if (adjustHeightTimer) {
		clearTimeout(adjustHeightTimer)
		adjustHeightTimer = null
	}
})

</script>

<style scoped lang="scss">
.fade-enter-active, .fade-leave-active {
	transition: opacity 0;
}
.fade-enter-from, .fade-leave-to {
	opacity: 0;
}

.vertical-container {
	display: flex;
	flex-direction: column;
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;
}

.vertical-block {
	display: flex;
	flex-direction: column;
	position: relative;
	width: 100%;
	overflow: auto;
}

.block-content {
	display: flex;
	flex-direction: column;
	flex: 1;
	padding: 0;
}

.chat-panel__no-access {
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 24px;
	text-align: center;
	background: #fff;
	border-radius: 12px;

	&-label {
		color: #979797;
		font-size: 16px;
		font-weight: 600;
		line-height: 20px;
	}
}

/* Свёрнутый блок занимает всю площадь панели, поэтому обёртки тултипа обязаны её пропустить. */
.collapsed-block-tooltip,
.collapsed-block-tooltip :deep(.app-tooltip__activator) {
	display: block;
	width: 100%;
	height: 100%;
}

.collapsed-block {
	display: flex;
	align-items: center;
	justify-content: center;
	color: #4D4D4D;
	font-size: 12px;
	line-height: 16px;
	cursor: pointer;
	height: 100%;
	background-color: #fff;
	border-radius: 8px;

	&__count {
		font-size: 12px;
		line-height: 16px;
		font-weight: 500;
		margin-right: 2px;
	}

	&__icon {
		margin-top: -2px;
	}
}

/* Вертикальный ресайзер */
.vertical-resizer {
	position: relative;
	height: 8px;
	background: transparent;
	cursor: row-resize;
	z-index: 10;
	opacity: 0;
}

.vertical-resizer:hover {
	opacity: 1;
	background: rgba(57, 103, 66, 0.2);
}

.vertical-resizer::after {
	content: '';
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	height: 4px;
	width: 40px;
	background: rgba(57, 103, 66, 0.4);
	border-radius: 1px;
}

.vertical-resizer:hover::after {
	background: rgba(57, 103, 66, 0.4);
	height: 4px;
	width: 60px;
	border-radius: 6px;
}

.vertical-resizer--disabled {
	cursor: default;
	pointer-events: none;
}

/* Визуальный вертикальный ресайзер */
.visual-resizer.vertical {
	position: fixed;
	height: 8px;
	width: 100%;
	background: transparent;
	cursor: row-resize;
	z-index: 1000;
	pointer-events: none;
}
</style>
